import React, { ReactNode, useState, useMemo, useEffect } from 'react';
import { providerService } from '../../api/provedor';
import type { Provider } from '../../api/provedor';
import {
  Factory,
  Search,
  Plus,
  Edit2,
  Trash2,
  Phone,
  Mail,
  MapPin,
  FileText,
  IdCard,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  Badge,
  Input,
  Label,
  Modal,
  ConfirmModal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

type FormErrors = {
  name?: string;
  cnpj?: string;
  address?: string;
  phone?: string;
  email?: string;
};

type FieldWrapperProps = {
  children: ReactNode;
  error?: string;
  label?: ReactNode;
  htmlFor?: string;
  required?: boolean;
};

const FieldWrapper = ({ children, error, label, htmlFor, required }: FieldWrapperProps) => (
  <div className="flex flex-col gap-1">
    {label ? (
      <Label htmlFor={htmlFor} className="mb-0.5">
        <span className="inline-flex items-center gap-0.5">
          {label}
          {required ? <span className="text-rose-500 ml-0.5 font-bold leading-none">*</span> : null}
        </span>
      </Label>
    ) : null}
    {children}
    {error ? (
      <div className="flex items-center gap-1 text-xs text-rose-600">
        <AlertCircle className="h-3 w-3 flex-shrink-0" />
        <span className="leading-tight">{error}</span>
      </div>
    ) : null}
  </div>
);

type CharCounterProps = { current: number; max: number };
const CharCounter = ({ current, max }: CharCounterProps) => (
  <div
    className={`mt-1 text-[10.5px] font-medium tabular-nums text-right pr-0.5 transition-colors ${
      current >= max ? 'text-rose-500' : current >= max * 0.9 ? 'text-amber-600' : 'text-slate-400'
    }`}
  >
    {current}/{max}
  </div>
);

function formatCNPJ(cnpj: string) {
  const digits = String(cnpj || '').replace(/\D/g, '').slice(0, 14);
  if (digits.length < 14) {
    return digits;
  }
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}

function formatPhone(phone: string) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return phone || '-';
}

const ProvedorPage = () => {
  const [provedores, setProvedores] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProvedor, setEditingProvedor] = useState<Provider | null>(null);
  const [formData, setFormData] = useState<Omit<Provider, 'id' | 'created_at' | 'updated_at'>>({
    name: '',
    cnpj: '',
    address: '',
    phone: '',
    email: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [provedorParaExcluir, setProvedorParaExcluir] = useState<Provider | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchProvedores();
  }, []);

  const fetchProvedores = async () => {
    setLoading(true);
    setError(null);
    try {
      const lista = await providerService.getAll();
      const sorted = [...lista].sort((a, b) => b.id - a.id);
      setProvedores(sorted);
    } catch (err) {
      console.error('Erro ao buscar fornecedores:', err);
      setError('Erro ao carregar fornecedores. Tente novamente mais tarde.');
      setProvedores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'cnpj') {
      const digits = value.replace(/\D/g, '').slice(0, 14);
      setFormData((prev) => ({ ...prev, [name]: formatCNPJ(digits) }));
      return;
    }
    if (name === 'phone') {
      const digits = value.replace(/\D/g, '').slice(0, 11);
      let formatted = digits;
      if (digits.length > 2) formatted = digits.replace(/(\d{2})(\d)/, '($1) $2');
      if (digits.length > 7) {
        formatted = formatted.replace(/(\(\d{2}\) \d{5})(\d{4})/, '$1-$2');
      } else if (digits.length > 6) {
        formatted = formatted.replace(/(\(\d{2}\) \d{4})(\d{4})/, '$1-$2');
      }
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};
    const name = formData.name.trim();
    const cnpj = formData.cnpj.replace(/\D/g, '');
    const address = formData.address.trim();
    const phone = (formData.phone || '').replace(/\D/g, '');
    const email = (formData.email || '').trim();

    if (!name) {
      errors.name = 'Informe a razão social / nome fantasia';
    } else if (name.length < 3) {
      errors.name = 'Nome muito curto (mínimo 3 caracteres)';
    } else if (name.length > 200) {
      errors.name = 'Nome excede 200 caracteres';
    }

    if (!cnpj) {
      errors.cnpj = 'Informe o CNPJ';
    } else if (cnpj.length !== 14) {
      errors.cnpj = 'CNPJ deve conter 14 dígitos';
    } else {
      const cnpjDuplicado = provedores.find(
        (p) => p.cnpj.replace(/\D/g, '') === cnpj && p.id !== editingProvedor?.id,
      );
      if (cnpjDuplicado) {
        errors.cnpj = 'Este CNPJ já está cadastrado';
      }
    }

    if (!address) {
      errors.address = 'Informe o endereço do fornecedor';
    } else if (address.length > 300) {
      errors.address = 'Endereço excede 300 caracteres';
    }

    if (phone && phone.length < 10) {
      errors.phone = 'Telefone inválido (mínimo 10 dígitos com DDD)';
    }

    if (email) {
      const emailRgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRgx.test(email)) {
        errors.email = 'E-mail inválido';
      } else if (email.length > 254) {
        errors.email = 'E-mail excede 254 caracteres';
      } else {
        const emailDuplicado = provedores.find(
          (p) => (p.email || '').toLowerCase() === email.toLowerCase() && p.id !== editingProvedor?.id,
        );
        if (emailDuplicado) {
          errors.email = 'Este e-mail já está cadastrado';
        }
      }
    }

    return errors;
  };

  const handleCreate = () => {
    setEditingProvedor(null);
    setFormData({ name: '', cnpj: '', address: '', phone: '', email: '' });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (provedor: Provider) => {
    setEditingProvedor(provedor);
    setFormData({
      name: provedor.name,
      cnpj: formatCNPJ(provedor.cnpj),
      address: provedor.address,
      phone: formatPhone(provedor.phone || ''),
      email: provedor.email || '',
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setEditingProvedor(null);
    setFormData({ name: '', cnpj: '', address: '', phone: '', email: '' });
    setFormErrors({});
  };

  const abrirConfirmacaoExclusao = (provedor: Provider) => {
    setProvedorParaExcluir(provedor);
    setIsDeleteConfirmOpen(true);
  };
  const cancelarExclusao = () => {
    if (deleteLoading) return;
    setIsDeleteConfirmOpen(false);
    setProvedorParaExcluir(null);
  };
  const confirmarExclusao = async () => {
    if (!provedorParaExcluir) return;
    setDeleteLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await providerService.delete(provedorParaExcluir.id);
      await fetchProvedores();
      setSuccessMessage('Fornecedor excluído com sucesso.');
      cancelarExclusao();
    } catch (err: unknown) {
      const e = err as any;
      setError(e?.response?.data?.message || e?.message || 'Erro ao excluir fornecedor');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const submitData = {
        ...formData,
        cnpj: formData.cnpj.replace(/\D/g, ''),
        phone: (formData.phone || '').replace(/\D/g, '') || undefined,
        email: formData.email || undefined,
      };
      if (editingProvedor) {
        await providerService.update(editingProvedor.id, submitData);
      } else {
        await providerService.create(submitData);
      }
      resetModal();
      await fetchProvedores();
      setSuccessMessage(
        editingProvedor ? 'Fornecedor atualizado com sucesso.' : 'Fornecedor cadastrado com sucesso.',
      );
    } catch (err: unknown) {
      const e = err as any;
      setError(e?.response?.data?.message || e?.message || 'Erro ao salvar fornecedor');
    } finally {
      setLoading(false);
    }
  };

  const provedoresFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return provedores;
    const term = searchTerm.toLowerCase().trim();
    return provedores.filter(
      (p) =>
        p.name.toLowerCase().includes(term) ||
        p.cnpj.toLowerCase().includes(term) ||
        (p.address && p.address.toLowerCase().includes(term)) ||
        (p.phone && p.phone.toLowerCase().includes(term)) ||
        (p.email && p.email.toLowerCase().includes(term)),
    );
  }, [provedores, searchTerm]);

  const totalPaginas = useMemo(
    () => Math.max(1, Math.ceil(provedoresFiltrados.length / ITEMS_PER_PAGE)),
    [provedoresFiltrados.length],
  );

  const { provedoresPaginados, pageRange } = useMemo(() => {
    const total = provedoresFiltrados.length;
    const safePage = Math.min(currentPage, totalPaginas);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      provedoresPaginados: provedoresFiltrados.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [provedoresFiltrados, currentPage, totalPaginas]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPaginas <= maxVisible + 2) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPaginas - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPaginas - 1) pages.push('...');
    pages.push(totalPaginas);
    return pages;
  }, [currentPage, totalPaginas]);

  useEffect(() => {
    if (currentPage > totalPaginas) setCurrentPage(1);
  }, [provedoresFiltrados.length, totalPaginas, currentPage]);

  useEffect(() => {
    if (!successMessage) return;
    const t = setTimeout(() => setSuccessMessage(null), 3500);
    return () => clearTimeout(t);
  }, [successMessage]);

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Fornecedores" hideTitleInBody />

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[15px] font-semibold text-slate-500">
              Gerencie parceiros, oficinas e prestadores de serviço
            </p>
            <h1 className="mt-1 text-[22px] sm:text-[26px] font-bold tracking-tight text-slate-900 leading-tight">
              Cadastre, edite e remova fornecedores.
              <span className="text-slate-500 font-semibold text-[18px] sm:text-[20px]">
                {' '}Centralize contatos para manutenções, abastecimentos e serviços.
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="primary"
              onClick={handleCreate}
              disabled={loading}
            >
              <Plus className="h-4 w-4 stroke-[1.8]" />
              Novo fornecedor
            </Button>
          </div>
        </div>

        {successMessage ? (
          <div className="mb-5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50/50 px-4.5 py-3.5 text-[13px] font-medium text-emerald-800 shadow-[0_2px_8px_-6px_rgba(16,185,129,0.25)]">
            {successMessage}
          </div>
        ) : null}
        {error ? (
          <div className="mb-5 rounded-xl border border-rose-200 bg-gradient-to-br from-rose-50 via-white to-rose-50/50 px-4.5 py-3.5 text-[13px] font-medium text-rose-800 shadow-[0_2px_8px_-6px_rgba(244,63,94,0.25)]">
            {error}
          </div>
        ) : null}

        <SectionCard>
          <CardHeader>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4 w-full">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.6)]">
                    <Factory className="h-[18px] w-[18px] stroke-[1.9]" />
                  </div>
                  <h2 className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                    Fornecedores cadastrados
                  </h2>
                </div>
                <p className="text-[13px] text-slate-500 mt-1.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhum fornecedor encontrado'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} fornecedores listados (total: ${provedores.length})`}
                </p>
              </div>
              <div className="w-full lg:max-w-md">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 h-4.5 w-4.5 -translate-y-1/2 stroke-[1.8] text-slate-400" />
                  <Input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar por nome, CNPJ, endereço, telefone ou email..."
                    className="!pl-11"
                    autoComplete="off"
                  />
                </div>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            <ModernTable className="table-fixed layout-fixed">
              <Thead>
                <Tr>
                  <Th className="w-[22%] min-w-[220px] max-w-[340px]">Fornecedor</Th>
                  <Th className="w-[15%] min-w-[150px] max-w-[200px]">CNPJ</Th>
                  <Th className="w-[24%] min-w-[200px] max-w-[380px]">Endereço</Th>
                  <Th className="w-[12%] min-w-[130px] max-w-[180px]">Telefone</Th>
                  <Th className="w-[18%] min-w-[180px] max-w-[300px]">Email</Th>
                  <Th className="!w-[120px] !min-w-[120px] !max-w-[150px]">Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {provedoresFiltrados.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-5 py-14 text-center text-sm text-slate-500"
                    >
                      {loading
                        ? 'Carregando...'
                        : searchTerm
                          ? 'Nenhum fornecedor corresponde à busca.'
                          : 'Nenhum fornecedor encontrado'}
                    </td>
                  </tr>
                ) : (
                  provedoresPaginados.map((provedor) => {
                    const cnpjFormatado = formatCNPJ(provedor.cnpj);
                    const tel = formatPhone(provedor.phone || '');
                    return (
                    <Tr key={provedor.id}>
                      <Td className="max-w-[340px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          <div className="flex items-center gap-3 min-w-0 w-full max-w-full">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.6)]">
                              <Factory className="h-[18px] w-[18px] stroke-[1.9]" />
                            </div>
                            <div className="min-w-0 flex-1 max-w-full">
                              <div
                                className="truncate font-semibold text-slate-900 w-full"
                                title={provedor.name}
                              >
                                {provedor.name}
                              </div>
                              <div
                                className="mt-0.5 flex items-center gap-1.5 text-[12px] text-slate-500 min-w-0 w-full"
                                title={provedor.address}
                              >
                                <MapPin className="h-3 w-3 shrink-0 stroke-[1.8]" />
                                <span className="truncate w-full">{provedor.address}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Td>
                      <Td className="max-w-[200px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          <span
                            className="block truncate font-mono text-[13px] font-semibold tracking-wide text-slate-700 tabular-nums whitespace-nowrap w-full"
                            title={cnpjFormatado}
                          >
                            {cnpjFormatado}
                          </span>
                        </div>
                      </Td>
                      <Td className="max-w-[380px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          {provedor.address ? (
                            <span
                              className="block truncate font-medium text-slate-700 w-full"
                              title={provedor.address}
                            >
                              {provedor.address}
                            </span>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </div>
                      </Td>
                      <Td className="max-w-[180px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          <span
                            className="block truncate font-medium text-slate-700 tabular-nums whitespace-nowrap w-full"
                            title={tel}
                          >
                            {tel}
                          </span>
                        </div>
                      </Td>
                      <Td className="max-w-[300px]">
                        <div className="min-w-0 w-full max-w-full overflow-hidden">
                          {provedor.email ? (
                            <span
                              className="block truncate font-medium text-slate-700 w-full"
                              title={provedor.email}
                            >
                              {provedor.email}
                            </span>
                          ) : (
                            <Badge tone="slate" className="!text-[12px]">
                              Sem email
                            </Badge>
                          )}
                        </div>
                      </Td>
                      <Td className="!max-w-[150px]">
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleEdit(provedor)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400/40 focus:ring-offset-1"
                            title="Editar fornecedor"
                            aria-label="Editar fornecedor"
                          >
                            <Edit2 className="h-[15px] w-[15px] stroke-[1.8]" />
                          </button>
                          <button
                            type="button"
                            onClick={() => abrirConfirmacaoExclusao(provedor)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400/40 focus:ring-offset-1"
                            title="Excluir fornecedor"
                            aria-label="Excluir fornecedor"
                          >
                            <Trash2 className="h-[15px] w-[15px] stroke-[1.8]" />
                          </button>
                        </div>
                      </Td>
                    </Tr>
                    );
                  })
                )}
              </Tbody>
            </ModernTable>

            {pageRange.total > 0 ? (
              <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[13px] font-medium text-slate-500">
                  Mostrando{' '}
                  <span className="font-semibold text-slate-700">
                    {pageRange.start}-{pageRange.end}
                  </span>{' '}
                  de{' '}
                  <span className="font-semibold text-slate-700">{pageRange.total}</span>{' '}
                  resultados
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    aria-label="Primeira página"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    aria-label="Página anterior"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                  <div className="mx-1 inline-flex items-center gap-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          aria-label={`Ir para página ${n}`}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                            n === currentPage
                              ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPaginas, p + 1))}
                    disabled={currentPage === totalPaginas}
                    aria-label="Próxima página"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPaginas)}
                    disabled={currentPage === totalPaginas}
                    aria-label="Última página"
                    className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                  >
                    <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>

        {isModalOpen && (
          <Modal
            onClose={resetModal}
            maxWidthClass="max-w-2xl"
            title={
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.65)]">
                  <Factory className="h-[18px] w-[18px]" />
                </div>
                <div>
                  <h3 className="text-[15px] font-bold tracking-tight text-slate-900 leading-tight">
                    {editingProvedor ? 'Editar fornecedor' : 'Novo fornecedor'}
                  </h3>
                  <p className="text-[12px] font-medium text-slate-500 mt-0.5 leading-snug">
                    {editingProvedor
                      ? 'Atualize os dados do prestador de serviço'
                      : 'Cadastre um novo fornecedor ou parceiro comercial'}
                  </p>
                </div>
              </div>
            }
            footer={
              <>
                <Button type="button" variant="secondary" onClick={resetModal} disabled={loading}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  form="provider-form"
                  variant="primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                      Salvando...
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {editingProvedor ? (
                        <Edit2 className="h-4 w-4 stroke-[1.8]" />
                      ) : (
                        <Plus className="h-4 w-4 stroke-[1.8]" />
                      )}
                      {editingProvedor ? 'Salvar alterações' : 'Cadastrar fornecedor'}
                    </span>
                  )}
                </Button>
              </>
            }
          >
            <form
              id="provider-form"
              onSubmit={(e) => void handleSubmit(e)}
              className="flex flex-col gap-6"
              noValidate
            >
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-orange-500 via-amber-500 to-orange-600" />
                  <h4 className="text-[13.5px] font-bold tracking-tight text-slate-800 uppercase">
                    Dados principais
                  </h4>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1.5">
                        <Factory className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                        Razão social / Nome fantasia
                      </span>
                    }
                    required
                    htmlFor="prov-name"
                    error={formErrors.name}
                  >
                    <Input
                      id="prov-name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          name: validateForm().name,
                        }))
                      }
                      placeholder="Ex: Auto Posto São José, Oficina Ferrari, Multilub..."
                      maxLength={200}
                      aria-invalid={!!formErrors.name}
                    />
                    <CharCounter current={formData.name.length} max={200} />
                  </FieldWrapper>

                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1.5">
                        <IdCard className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                        CNPJ
                      </span>
                    }
                    required
                    htmlFor="prov-cnpj"
                    error={formErrors.cnpj}
                  >
                    <Input
                      id="prov-cnpj"
                      name="cnpj"
                      type="text"
                      value={formData.cnpj}
                      onChange={handleInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          cnpj: validateForm().cnpj,
                        }))
                      }
                      placeholder="00.000.000/0000-00"
                      maxLength={18}
                      inputMode="numeric"
                      aria-invalid={!!formErrors.cnpj}
                    />
                    <CharCounter current={formData.cnpj.replace(/\D/g, '').length} max={14} />
                  </FieldWrapper>
                </div>

                <FieldWrapper
                  label={
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                      Endereço completo
                    </span>
                  }
                  required
                  htmlFor="prov-address"
                  error={formErrors.address}
                >
                  <Input
                    id="prov-address"
                    name="address"
                    type="text"
                    value={formData.address}
                    onChange={handleInputChange}
                    onBlur={() =>
                      setFormErrors((prev) => ({
                        ...prev,
                        address: validateForm().address,
                      }))
                    }
                    placeholder="Av. Paulista, 1000 – Centro – São Paulo/SP"
                    maxLength={300}
                    aria-invalid={!!formErrors.address}
                  />
                  <CharCounter current={formData.address.length} max={300} />
                </FieldWrapper>
              </div>

              <div className="mx-0 -my-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <div className="h-7 w-1.5 shrink-0 rounded-full bg-gradient-to-b from-emerald-500 via-emerald-500 to-teal-500" />
                  <h4 className="text-[13.5px] font-bold tracking-tight text-slate-800 uppercase">
                    Contato
                  </h4>
                </div>

                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                        Telefone / WhatsApp
                      </span>
                    }
                    htmlFor="prov-phone"
                    error={formErrors.phone}
                  >
                    <Input
                      id="prov-phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          phone: validateForm().phone,
                        }))
                      }
                      placeholder="(11) 90000-0000"
                      maxLength={15}
                      inputMode="tel"
                      aria-invalid={!!formErrors.phone}
                    />
                  </FieldWrapper>

                  <FieldWrapper
                    label={
                      <span className="inline-flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                        E-mail
                      </span>
                    }
                    htmlFor="prov-email"
                    error={formErrors.email}
                  >
                    <Input
                      id="prov-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={() =>
                        setFormErrors((prev) => ({
                          ...prev,
                          email: validateForm().email,
                        }))
                      }
                      placeholder="contato@fornecedor.com.br"
                      maxLength={254}
                      aria-invalid={!!formErrors.email}
                    />
                    <CharCounter current={(formData.email || '').length} max={254} />
                  </FieldWrapper>
                </div>

                <div className="rounded-xl border border-sky-200/60 bg-gradient-to-br from-sky-50/70 via-white to-blue-50/50 p-4 text-[13px] leading-relaxed text-sky-900 shadow-sm">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-500/15 text-sky-600 ring-1 ring-inset ring-sky-500/20">
                      <FileText className="h-4 w-4 stroke-[1.8]" />
                    </div>
                    <div>
                      <p className="font-bold tracking-tight">Informações de contato</p>
                      <p className="mt-0.5 text-sky-900/90">
                        O e-mail é opcional, mas recomendado para envio de cotações, ordens de serviço e
                        notificações automáticas.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </Modal>
        )}

        <ConfirmModal
          isOpen={isDeleteConfirmOpen}
          title={
            provedorParaExcluir
              ? `Excluir ${provedorParaExcluir.name}?`
              : 'Excluir fornecedor?'
          }
          description={
            provedorParaExcluir ? (
              <span>
                Você está prestes a excluir o fornecedor{' '}
                <strong className="font-semibold text-slate-700">
                  {provedorParaExcluir.name}
                </strong>
                {' '}(CNPJ: <span className="font-mono font-semibold text-slate-700">{formatCNPJ(provedorParaExcluir.cnpj)}</span>).
                Históricos de manutenção ou abastecimento vinculados a este fornecedor serão preservados,
                mas novas atribuições não serão possíveis.
              </span>
            ) : (
              <span>
                Históricos de manutenção ou abastecimento vinculados a este fornecedor serão preservados,
                mas novas atribuições não serão possíveis.
              </span>
            )
          }
          confirmLabel={
            deleteLoading ? (
              <span className="inline-flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                Excluindo...
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Trash2 className="h-4 w-4 stroke-[1.8]" />
                Sim, excluir fornecedor
              </span>
            )
          }
          cancelLabel="Cancelar"
          confirmVariant="danger"
          onConfirm={confirmarExclusao}
          onCancel={cancelarExclusao}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default ProvedorPage;
